import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import { Attribute } from './attribute'
import { Dimension } from './dimension'
import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import type { Term } from './term'
import { Type } from './type'

export interface Entity<TId extends Entity.Id = Entity.Id> extends Model<TId> {
  /**
   * Larger numbers equal lower placement in the hierarchy. 0 = root.
   */
  level: number
  attributes?: readonly Attribute[]
}
export namespace Entity {
  export const isEntity: TypeChecker<Entity> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.intersection(
      Model.getTypeChecker(Id.isId),
      TypeChecker.Factory.object<Difference<Entity, Model<Id>>>({
        attributes: TypeChecker.Factory.optional(
          TypeChecker.Factory.list(Attribute.isAttribute),
        ),
        level: TypeChecker.isNumber,
      }),
    ),
  ).usingAlias('Entity')
  /**
   * Just checks if the object's ID indicates that it is an entity. This can be
   * used to distinguish between various model types without checking that every
   * field of the model is correct.
   */
  export const shouldBeEntity = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Entity),
  )

  type InputBase = Omit<Entity, 'id' | 'attributes' | 'terms'> & {
    termIds?: readonly Term.Id[]
  }
  export interface Input extends InputBase {
    partialId?: PartialId
    /**
     * Entities inherit certain attributes. These are additional attributes to
     * add with those inheritted attributes
     */
    additionalAttributes?: readonly Attribute.Input.InContext[]
    dimension: Dimension.Identifiable
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isEntity, ['id', 'attributes']),
        TypeChecker.Factory.object<Difference<Input, InputBase>>({
          additionalAttributes: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Attribute.Input.InContext.isInContext),
          ),
          dimension: Dimension.Identifiable.isIdentifiable,
          partialId: TypeChecker.Factory.optional(PartialId.isPartialId),
        }),
      ),
    ).usingAlias('Entity.Input')

    export type InContext = Omit<Entity.Input, 'dimension' | 'level'>
    export namespace InContext {
      export const isInContext: TypeChecker<InContext> =
        TypeChecker.Factory.omit(isInput, ['dimension', 'level'])
    }
  }

  type UpdatesBase = Partial<InputBase>
  export type Updates = UpdatesBase
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(isEntity, ['id', 'attributes']),
          // NOTE: if we ever need to support more updates, add properties for
          // type checking below
          TypeChecker.Factory.object<Difference<Updates, UpdatesBase>>({}),
        ),
    ).usingAlias('Entity.Updates')
  }

  export type Identifiable<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > =
    | _Identifiable.ForSpecificType<Type.Entity, TPartialId, TContext>
    // We can get entity ID from attributes on entities
    | Attribute.Identifiable<
        PartialId,
        _Id.UsingSeparator.On<
          Id<TPartialId, TContext>,
          _Id.Separator.WithinSection
        >
      >
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          _Identifiable.ForSpecificType.getTypeChecker(
            Type.Entity,
            Id.Context.isContext,
          ),
          TypeChecker.Factory.extract(
            Attribute.Identifiable.isIdentifiable,
            _Identifiable.getTypeChecker(
              Type.Entity,
              Attribute.Id.Context.Using.getTypeChecker(Id.isId),
            ),
          ),
        ),
      ).usingAlias('dms.models.Entity.Identifiable')
  }

  export type Id<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = _Id<Type.Entity, TPartialId, TContext>
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Entity, Context.isContext),
    ).usingAlias('Entity.Id') as TypeChecker<Id>
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId, infer TContext>
        ? Id<TPartialId, TContext>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(
      source: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined
    export function from(
      source: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined {
      if (!source) {
        return undefined
      }
      if (Attribute.Identifiable.isIdentifiable(source)) {
        // For attributes, we get the entity ID from the attribute context
        const context = Attribute.Id.Context.from(source)
        if (Attribute.Id.Context.ForAll.isForAll(context)) {
          return undefined
        }
        const holderId = _Id.Separator.swap(
          context,
          _Id.Separator.WithinSection,
          _Id.Separator.BetweenSections,
        )
        // If the holder is not an entity ID, we return `undefined`
        return Entity.Id.isId(holderId) ? holderId : undefined
      }
      // If this is not an attribute, we should use the standard
      // Identifiable->ID function
      return _Id.from<Type.Entity, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Entity),
      )
    }

    /**
     * {@link PartialId} of {@link Dimension} containing this entity
     */
    export type Context = PartialId
    export namespace Context {
      export const isContext: TypeChecker<Context> = PartialId.isPartialId

      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from(
        source: Identifiable | Dimension.Identifiable,
      ): Context {
        if (Identifiable.isIdentifiable(source)) {
          return _Id.Context.from(Id.from(source))
        }
        return _Id.Parts.from(Dimension.Id.from(source)).partialId
      }
    }
  }

  /**
   * These {@link Attribute} should exist on all {@link Entity}
   *
   * __NOTE:__ this is a function to avoid runtime issues from circular dependencies
   */
  export function getBuiltInAttributeInputs(): readonly Attribute.Input[] {
    const context = Attribute.Id.Context.ForAll.of(Type.Entity)
    return (
      [
        Attribute.Input.FOR_ID,
        {
          canBeOptional: false,
          // default: new Multirange(Range.everything()),
          default: '{(,)}', // TODO: use above
          description:
            'Period(s) of time when this item is actively part of the institution',
          isOptional: false,
          name: 'Active Period(s)',
          partialId: PartialId.Standard.create('active_periods'),
          type: common.value.types.Intrinsic.DateMultirange,
        },
        {
          canBeOptional: false,
          description:
            'Usually represents an indentifier from outside the system',
          isOptional: false,
          name: 'Code',
          partialId: PartialId.Standard.create('code'),
          type: common.value.types.Intrinsic.Text,
        },
        {
          canBeOptional: false,
          description: 'Display name',
          isOptional: false,
          name: 'Name',
          partialId: PartialId.Standard.create('name'),
          type: common.value.types.Intrinsic.Text,
        },
        {
          canBeOptional: true,
          description: 'Describes this item',
          isOptional: true,
          name: 'Description',
          partialId: PartialId.Standard.create('descr'),
          type: common.value.types.Intrinsic.Text,
        },
        {
          canBeOptional: true,
          computation: {
            definition: {
              selection: {
                '++': [{ '@.': '!name' }, ' ', { '@.': '!code' }],
              },
            },
          },
          description:
            'Auto-generated search field combining entity name and code to optimize text-based queries and improve discoverability',
          isOptional: true,
          name: 'Search String',
          partialId: PartialId.Standard.create('search_string'),
          type: common.value.types.Intrinsic.Text,
        },
      ] satisfies readonly Attribute.Input.InContext[]
    ).map<Attribute.Input>(input => ({ ...input, context }))
  }
}
